import React, { useState } from "react";
import type { ProcessedRaceMeeting } from "@/hooks/firestore/v2/betting/useRaceMeetings";
import { Box } from "@/components/Box";
import { Row } from "@/features/racing/components/Row";
import { useTitle } from "@/hooks/graphql/useTitles";
import { useIsLoggedIn } from "@/hooks/useIsLoggedIn";
import { TitleIcon } from "@/components/TitleIcon";
import { SmallPromoIcon } from "@/components/PromoIcon/SmallPromoIcon";
import { CountryAndState } from "@/features/racing/components/CountryAndState";
import { EventStatus, type StatusEnum } from "@/components/layout/EventStatus";
import { MoreInfo } from "@/components/MoreInfo";
import { Caret } from "@/components/Caret";
import { Clamp } from "@/components/Clamp";
import { Collapse } from "@/components/Collapse";

import * as styles from "./MeetingGroup.module.scss";
import { raceNumber } from "./Row.module.scss";

const humanReadableTitles: Record<string, string> = {
  HORSE_RACING: "Thoroughbred Racing",
  GREYHOUNDS: "Greyhound Racing",
  HARNESS_RACING: "Harness Racing",
};

type MeetingGroupProps = {
  title: string;
  meetings: ProcessedRaceMeeting[];
};

const MeetingGroup = ({ title, meetings }: MeetingGroupProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const isLoggedIn = useIsLoggedIn();
  const humanTitle = humanReadableTitles[title];
  const titleInfo = useTitle(title.toLowerCase());

  return (
    <Box
      clampClassName={styles.clampContainer}
      noChildrenSpace
      icon={
        <div className={styles.groupIcon}>
          <TitleIcon title={titleInfo} svg />
        </div>
      }
      title={humanTitle}
      clamp={
        <Clamp
          onClick={() => setIsOpen(!isOpen)}
          variant="grey"
          className={styles.clamp}
        >
          <Caret variant={isOpen ? "up" : "down"} />
        </Clamp>
      }
    >
      <Collapse isOpen={isOpen} skipInitial>
        {meetings.map((meeting) => (
          <Row
            key={`meeting-${meeting.id}`}
            isComplete={[
              "CANCELLED",
              "CLOSED",
              "FINAL",
              "ABANDONED",
              "DONE",
              "INTERIM",
            ].includes(meeting.currentRace.status)}
            className={styles.row}
            to={`/racing/betting/meeting/${meeting.id}`}
            icon={
              <>
                <TitleIcon title={titleInfo} svg />
                {isLoggedIn && meeting.showPromoBanner ? (
                  <SmallPromoIcon />
                ) : null}
              </>
            }
            header={
              <>
                {meeting.currentRace.number ? (
                  <div className={raceNumber}>
                    R{meeting.currentRace.number}
                  </div>
                ) : null}
                {meeting.venue}
              </>
            }
            subHeader={
              <CountryAndState
                countryCode={meeting.country}
                stateCode={meeting.state}
              />
            }
            clamp={
              <>
                <EventStatus
                  status={meeting.currentRace.status as unknown as StatusEnum} // TODO: need to fix status enum to receive values from racing and betting event statuses
                  scheduledStartTime={meeting.currentRace.scheduledStartTimeTs.toDate()}
                />
                <MoreInfo direction="horizontal" />
              </>
            }
          />
        ))}
      </Collapse>
    </Box>
  );
};

export { MeetingGroup };
